import type { OrderManagementContractList } from "@/types/api/data-contracts";
import { ServiceFeeTypeEnum } from "@/types/api/data-contracts";
import type { IntPrice } from "@/types";
import { convertIntToFloatPrice } from "@/utils/pricing/convertIntToFloatPrice";
import { formatPriceWithCurrency } from "@/utils/formatPriceWithCurrency";

const SERVICE_FEE_TYPE_SHORT_LABELS: Record<ServiceFeeTypeEnum, string> = {
    [ServiceFeeTypeEnum.OrderManagementOnly]: "OM Only",
    [ServiceFeeTypeEnum.OrderManagementWithPaymentSupport]: "OM With Payment",
};

/**
 * Formats an order management contract for display in a dropdown label.
 * Format: "{contract_number} - {formatted_price} - {fee1}, {fee2}"
 *
 * @param contract - The order management contract to format
 * @returns A formatted string for the dropdown label
 */
export const formatOrderManagementIdentifierForDropdownLabel = (
    contract: OrderManagementContractList
): string => {
    const formattedPrice = formatPriceWithCurrency(
        convertIntToFloatPrice(contract.total_contract_value as IntPrice),
        contract.currency
    );

    const feeStrings = contract.service_fees
        .filter((fee) => fee.service_fee_type && fee.service_fee_percentage)
        .map((fee) => {
            const label = SERVICE_FEE_TYPE_SHORT_LABELS[fee.service_fee_type!];
            const percentage = Number(fee.service_fee_percentage) * 100;
            return `${label} ${Number.isNaN(percentage) ? 0 : percentage}%`;
        });

    const base = `${contract.contract_number} - ${formattedPrice}`;

    return feeStrings.length > 0 ? `${base} - ${feeStrings.join(", ")}` : base;
};
